import React, { useContext, useMemo, useState } from 'react';
import { Alert, StyleSheet, View } from 'react-native';
import { ActivityIndicator, Button, Text } from 'react-native-paper';
import { useTranslation } from 'react-i18next';

import JobsContext from '../../context/JobsContext';
import { ResumeDocument } from '../../types';
import { Palette, radius, spacing, useThemePalette } from '../../theme';
import { exportToDocx, exportToPdf, ExportLabels } from '../../lib/resume/export';
import ModalSheet from '../ModalSheet';

interface Props {
  visible: boolean;
  onDismiss: () => void;
  resume: ResumeDocument;
}

const ExportSheet: React.FC<Props> = ({ visible, onDismiss, resume }) => {
  const { t } = useTranslation();
  const palette = useThemePalette();
  const styles = useMemo(() => makeStyles(palette), [palette]);
  const [busy, setBusy] = useState<'pdf' | 'docx' | null>(null);
  const { hideAds, removeAdsEntitled } = useContext(JobsContext);
  const adsHidden = hideAds || removeAdsEntitled;

  const labels: ExportLabels = {
    contact: t('resume.sections.contact'),
    summary: t('resume.sections.summary'),
    experience: t('resume.sections.experience'),
    education: t('resume.sections.education'),
    skills: t('resume.sections.skills'),
    languages: t('resume.sections.languages'),
    links: t('resume.sections.links'),
    present: t('resume.fields.present'),
  };

  const run = async (kind: 'pdf' | 'docx') => {
    if (busy) return;
    setBusy(kind);
    try {
      if (kind === 'pdf') await exportToPdf(resume, labels, adsHidden);
      else await exportToDocx(resume, labels, adsHidden);
      onDismiss();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      Alert.alert(t('resume.export.errorTitle'), message || t('resume.export.errorMessage'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <ModalSheet visible={visible} onDismiss={busy ? () => {} : onDismiss} cardStyle={styles.card}>
      <View style={styles.body}>
        <Text style={styles.title}>{t('resume.buttons.export')}</Text>
        <Text style={styles.subtitle}>{t('resume.export.preparingHint')}</Text>

        <Button
          mode="contained"
          icon="file-pdf-box"
          onPress={() => run('pdf')}
          loading={busy === 'pdf'}
          disabled={busy !== null}
          buttonColor={palette.primary}
          textColor="#0F1115"
          style={styles.btn}
          contentStyle={styles.btnContent}
        >
          {t('resume.buttons.exportPdf')}
        </Button>
        <Button
          mode="contained"
          icon="file-word-box"
          onPress={() => run('docx')}
          loading={busy === 'docx'}
          disabled={busy !== null}
          buttonColor={palette.elevated}
          textColor={palette.textOnDark}
          style={styles.btn}
          contentStyle={styles.btnContent}
        >
          {t('resume.buttons.exportDocx')}
        </Button>

        {busy && (
          <View style={styles.busyRow}>
            <ActivityIndicator color={palette.primary} />
            <Text style={styles.busyText}>{t('resume.export.preparing')}</Text>
          </View>
        )}

        <Button
          mode="text"
          onPress={onDismiss}
          textColor={palette.textPrimary}
          disabled={busy !== null}
          style={styles.cancel}
        >
          {t('common.cancel')}
        </Button>
      </View>
    </ModalSheet>
  );
};

const makeStyles = (p: Palette) =>
  StyleSheet.create({
    card: { backgroundColor: p.surface, borderRadius: radius.lg },
    body: { padding: spacing.lg, gap: spacing.sm },
    title: { fontSize: 20, fontWeight: '700', color: p.textPrimary },
    subtitle: { fontSize: 13, color: p.textMuted, marginBottom: spacing.sm },
    btn: { borderRadius: radius.pill },
    btnContent: { paddingVertical: spacing.xs },
    busyRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      marginTop: spacing.sm,
    },
    busyText: { color: p.textSecondary, fontSize: 13 },
    cancel: { marginTop: spacing.xs },
  });

export default ExportSheet;
